import { useEffect, useState } from "react";
import { usePolling } from "../lib/use-polling";
import { api, ApiError } from "../lib/api";
import { PageHelp } from "../components/PageHelp";
import { Table, type Column } from "../components/primitives/Table";
import { Button } from "../components/primitives/Button";
import { Badge } from "../components/primitives/Badge";
import { Pulse } from "../components/primitives/Pulse";
import { InfoTip } from "../components/primitives/InfoTip";
import { FormField, TextInput } from "../components/primitives/FormField";
import { PageHeader } from "../components/primitives/PageHeader";
import { useConfirm } from "../components/primitives/Confirm";

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/;

interface Org { id: string; name: string }

interface EnvRow {
  id: string;
  name: string;
  displayName?: string;
  organizationId: string;
  disabled?: boolean;
  pipelineCount?: number;
}

/**
 * The environment rows a client's pipelines can be labelled with.
 *
 * An environment is a tag on the data, not a separate deployment — the page
 * copy says so, because the word invites the other reading.
 */
export function Environments() {
  const q = usePolling(() => api.listEnvironments(), 30000);
  const [showNew, setShowNew] = useState(false);
  const [orgs, setOrgs] = useState<Org[] | null>(null);
  const [organizationId, setOrganizationId] = useState("");
  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Immediate feedback for the active/paused toggle: overlay the operator's
  // intent on the polled rows until the next poll (≤30s) confirms it server-side.
  const [disabledOverride, setDisabledOverride] = useState<Record<string, boolean>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const confirm = useConfirm();

  useEffect(() => {
    if (!showNew) return;
    api.listOrganizations().then((r) => setOrgs(r.organizations as Org[])).catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [showNew]);

  // Once a poll returns rows whose `disabled` already matches an overlay, drop
  // that overlay — the server is now the source of truth again.
  useEffect(() => {
    if (q.status !== "ready") return;
    setDisabledOverride((m) => {
      if (Object.keys(m).length === 0) return m;
      const next: Record<string, boolean> = {};
      for (const row of q.data.environments as EnvRow[]) {
        if (row.id in m && m[row.id] !== (row.disabled ?? false)) next[row.id] = m[row.id];
      }
      return next;
    });
  }, [q]);

  /** The first thing wrong with the new-environment form, or null when it is ready. */
  function validate(): string | null {
    if (!organizationId) return "Pick an organization.";
    if (!name) return "Name is required.";
    if (!SLUG_RE.test(name)) return "Use lowercase letters, digits, and hyphens. 3 to 63 chars.";
    return null;
  }

  /** Create the environment and clear the form for the next one. */
  async function create() {
    const v = validate();
    if (v) { setError(v); return; }
    setError(null);
    try {
      await api.createEnvironment({
        organizationId,
        name,
        displayName: displayName || undefined,
      });
      setShowNew(false);
      setOrganizationId("");
      setName("");
      setDisplayName("");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  /** Flip an environment between open and closed to new pipelines, showing the
   *  new state at once and rolling that overlay back if the call does not land. */
  async function toggleDisabled(row: EnvRow) {
    const next = !(disabledOverride[row.id] ?? row.disabled ?? false);
    setBusyId(row.id);
    setDisabledOverride((m) => ({ ...m, [row.id]: next }));
    try {
      await api.setEnvironmentDisabled(row.id, next);
    } catch (err: unknown) {
      // Roll the overlay back to what it was.
      setDisabledOverride((m) => {
        const { [row.id]: _dropped, ...rest } = m;
        return rest;
      });
      setError(`Change did not complete: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusyId(null);
    }
  }

  /** Delete an environment, refusing up front while pipelines still route through
   *  it — the API refuses too, but saying so here costs no round trip. */
  async function remove(row: EnvRow) {
    const count = row.pipelineCount ?? 0;
    if (count > 0) {
      setError(`"${row.name}" still routes ${count} pipeline${count === 1 ? "" : "s"}. Re-point or remove those pipelines first.`);
      return;
    }
    setError(null);
    await confirm({
      tier: 1,
      title: `Delete "${row.name}"?`,
      body: "New pipelines can no longer be created in this environment. Existing pipelines are unaffected: deletion is refused while any still reference it.",
      confirmLabel: "Delete environment",
      onConfirm: async () => {
        try {
          await api.deleteEnvironment(row.id);
        } catch (err: unknown) {
          if (err instanceof ApiError && err.code === "environment.in-use") throw new Error(err.message);
          throw err;
        }
      },
    });
  }

  if (q.status === "loading") return <Pulse />;

  const rows: EnvRow[] = (q.data?.environments ?? []) as EnvRow[];

  const columns: Column<EnvRow>[] = [
    { key: "name", label: "NAME", mono: true, render: (r) => r.name },
    { key: "displayName", label: "DISPLAY", render: (r) => r.displayName ?? "—" },
    { key: "org", label: "ORG", mono: true, render: (r) => String(r.organizationId).slice(0, 8) },
    {
      key: "pipelines",
      label: "PIPELINES",
      mono: true,
      render: (r) => String(r.pipelineCount ?? 0),
    },
    {
      key: "status",
      label: "STATUS",
      render: (r) => {
        const disabled = disabledOverride[r.id] ?? r.disabled ?? false;
        return <Badge tone={disabled ? "muted" : "ok"}>{disabled ? "Paused" : "Active"}</Badge>;
      },
    },
    {
      key: "actions",
      label: "",
      render: (r) => {
        const disabled = disabledOverride[r.id] ?? r.disabled ?? false;
        const busy = busyId === r.id;
        const referenced = (r.pipelineCount ?? 0) > 0;
        return (
          <div style={{ display: "inline-flex", gap: 8 }}>
            <Button variant="secondary" disabled={busy} onClick={() => toggleDisabled(r)}>
              {disabled ? "Resume" : "Pause"}
            </Button>
            <Button
              variant="ghost"
              disabled={busy || referenced}
              title={referenced ? "In use by one or more pipelines" : undefined}
              onClick={() => remove(r)}
            >
              Delete
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow={
          <>
            Advanced
            <InfoTip termKey="environment" />
          </>
        }
        title="Environments"
        actions={<Button onClick={() => { setError(null); setShowNew((s) => !s); }}>{showNew ? "Cancel" : "New environment"}</Button>}
      />
      <PageHelp />
      {error && !showNew && <div className="field__error" style={{ marginBottom: 12 }}>{error}</div>}

      {/* Isolation context — read-only. Plain language, one ⓘ to the definition. */}
      <p className="page-help" style={{ display: "flex", alignItems: "flex-start", gap: 6, margin: "0 0 16px", maxWidth: 760 }}>
        <span>
          Events are routed by the account they come from. A pipeline only sees an event when its
          environment name ends in that account's tag — so a name like <span className="mono">rip-city-production</span> is
          matched by events from the production account, and staging events never reach it.
        </span>
        <InfoTip termKey="environmentIsolation" />
      </p>

      {showNew && (
        <div style={{ border: "1px solid var(--color-border)", padding: 16, marginBottom: 24, maxWidth: 720 }}>
          <div className="uppercase-label" style={{ marginBottom: 8 }}>NEW ENVIRONMENT</div>
          <p className="page-help" style={{ margin: "0 0 12px" }}>
            Environments scope a pipeline to a customer or workload — production, staging, denver-only.
            They are a data attribute, not a deployment target. Pipelines reference an environment by name;
            creating a pipeline against an environment that does not exist or is paused fails validation.
            Name is fixed once created — pipelines reference it — so pick it deliberately.
          </p>
          <FormField label="CLIENT *" hint="Pick the client this environment belongs to.">
            <select
              className="field__input"
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
            >
              <option value="">—</option>
              {(orgs ?? []).map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </FormField>
          <FormField label="NAME *" hint="Lowercase, hyphenated, ending in the account tag. e.g. rip-city-production, denver-staging.">
            <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="rip-city-production" />
          </FormField>
          <FormField label="DISPLAY NAME" hint="Optional. Free-form, shown in dashboards. Defaults to NAME.">
            <TextInput value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Rip City — Production" />
          </FormField>
          {error && <div className="field__error" style={{ marginBottom: 12 }}>{error}</div>}
          <Button variant="primary" onClick={create}>CREATE</Button>
        </div>
      )}
      <Table
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        empty={<div className="page-help">Nothing scoped yet. Create an environment to pin pipelines to a client or stage.</div>}
      />
    </div>
  );
}
